/**
 * TanStack Router configuration for the SmartSafe Portal SPA.
 *
 * Implements:
 * - Type-safe route definitions
 * - Route-based code splitting (lazy loading per route)
 * - Role-based beforeLoad guards: createRoleGuard([...rolesFor(path)]), single source ROUTE_ACCESS
 * - Layout route with AppShell for protected pages
 * - Login/403 routes render without AppShell
 *
 * Requirements: 1.3, 2.1, 2.3, 31.1, 32.1, 33.1, 34.1
 */

import { createRouter, createRootRoute, createRoute, Outlet, redirect } from '@tanstack/react-router';
import { lazy, Suspense } from 'react';

import { createRoleGuard } from './role-guard';
import { rolesFor } from './route-config';
import { AppShell } from '../components/AppShell';

// Lazy-loaded route components for code splitting
const DashboardPage = lazy(() => import('../pages/DashboardPage'));
const AlertsPage = lazy(() => import('../pages/AlertsPage'));
const MapPage = lazy(() => import('../pages/MapPage'));
// Hidden until AI feature is complete (see handoff.md)
// const AiInsightsPage = lazy(() => import('../pages/AiInsightsPage'));
const EquipamentoPage = lazy(() => import('../pages/EquipamentoPage'));
const EquipamentosListPage = lazy(() => import('../pages/EquipamentosListPage'));
const FinanceiroPage = lazy(() => import('../pages/FinanceiroPage'));
const ColetasPage = lazy(() => import('../pages/ColetasPage'));
const SensoresPage = lazy(() => import('../pages/SensoresPage'));
const SuporteTecnicoPage = lazy(() => import('../pages/SuporteTecnicoPage'));
const EstoquePage = lazy(() => import('../pages/EstoquePage'));
const HelpDeskPage = lazy(() => import('../pages/HelpDeskPage'));
const ReportsPage = lazy(() => import('../pages/ReportsPage'));
const AuditPage = lazy(() => import('../pages/AuditPage'));
const LgpdPage = lazy(() => import('../pages/LgpdPage'));
const PessoasPage = lazy(() => import('../pages/PessoasPage'));
const DesignadoresPage = lazy(() => import('../pages/DesignadoresPage'));
const EmpresasPage = lazy(() => import('../pages/EmpresasPage'));
const FiliaisPage = lazy(() => import('../pages/FiliaisPage'));
const DevicesPage = lazy(() => import('../pages/DevicesPage'));
const UtilizadoresPage = lazy(() => import('../pages/UtilizadoresPage'));
const ConfiguracoesPage = lazy(() => import('../pages/ConfiguracoesPage'));
const ConfigPlataformaPage = lazy(() => import('../pages/ConfigPlataformaPage'));
const OtaFirmwarePage = lazy(() => import('../pages/OtaFirmwarePage'));
const ApiDocsPage = lazy(() => import('../pages/ApiDocsPage'));
const ApiKeysPage = lazy(() => import('../pages/ApiKeysPage'));
const WebhooksPage = lazy(() => import('../pages/WebhooksPage'));
const SandboxPage = lazy(() => import('../pages/SandboxPage'));
const EventLogPage = lazy(() => import('../pages/EventLogPage'));
const NovoEquipamentoPage = lazy(() => import('../pages/NovoEquipamentoPage'));
const ForbiddenPage = lazy(() => import('../pages/ForbiddenPage'));
const LoginPage = lazy(() => import('../pages/LoginPage'));
const CapacidadesPage = lazy(() => import('../pages/CapacidadesPage'));

/**
 * Root route - no layout shell. Login/403 render directly here.
 */
export const rootRoute = createRootRoute({
  component: () => (
    <Suspense fallback={null}>
      <Outlet />
    </Suspense>
  ),
});

/**
 * Layout route - wraps protected routes with AppShell (sidebar + topbar).
 */
const layoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'layout',
  component: () => (
    <AppShell>
      <Suspense fallback={<div style={{ padding: '24px', color: 'var(--text-2)' }}>Carregando...</div>}>
        <Outlet />
      </Suspense>
    </AppShell>
  ),
});

// ═══════════════════════════════════════════
// MONITORAMENTO
// ═══════════════════════════════════════════

const dashboardRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/',
  beforeLoad: createRoleGuard([...rolesFor('/')]),
  component: DashboardPage,
});

const sensoresRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/sensores',
  beforeLoad: createRoleGuard([...rolesFor('/sensores')]),
  component: SensoresPage,
});

const financeiroRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/financeiro',
  beforeLoad: createRoleGuard([...rolesFor('/financeiro')]),
  component: FinanceiroPage,
});

const equipamentosListRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/equipamentos',
  beforeLoad: createRoleGuard([...rolesFor('/equipamentos')]),
  component: EquipamentosListPage,
  validateSearch: (
    search: Record<string, unknown>,
  ): {
    tab?: 'cadastro' | 'monitoramento' | undefined;
    filialId?: string | undefined;
    operadorId?: string | undefined;
  } => ({
    tab: search.tab === 'cadastro' || search.tab === 'monitoramento' ? search.tab : undefined,
    filialId: typeof search.filialId === 'string' && search.filialId ? search.filialId : undefined,
    operadorId: typeof search.operadorId === 'string' && search.operadorId ? search.operadorId : undefined,
  }),
});

const equipamentoDetailRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/equipamentos/$deviceId',
  beforeLoad: createRoleGuard([...rolesFor('/equipamentos/$deviceId')]),
  component: EquipamentoPage,
});

const alertsRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/alerts',
  beforeLoad: createRoleGuard([...rolesFor('/alerts')]),
  component: AlertsPage,
});

const coletasRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/coletas',
  beforeLoad: createRoleGuard([...rolesFor('/coletas')]),
  component: ColetasPage,
});

// ═══════════════════════════════════════════
// RELATÓRIOS
// ═══════════════════════════════════════════

const reportsRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/reports',
  beforeLoad: createRoleGuard([...rolesFor('/reports')]),
  component: ReportsPage,
});

const caixaRedirect = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/caixa',
  beforeLoad: () => {
    // eslint-disable-next-line @typescript-eslint/only-throw-error -- TanStack redirect() is the idiomatic control-flow throw
    throw redirect({ to: '/financeiro' });
  },
});

// Hidden until AI feature is complete (see handoff.md)
// const aiRoute = createRoute({
//   getParentRoute: () => layoutRoute,
//   path: '/ai',
//   beforeLoad: createRoleGuard(['platform_admin', 'operator', 'client_admin', 'client_viewer']),
//   component: AiInsightsPage,
// });

const suporteTecnicoRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/suporte-tecnico',
  beforeLoad: createRoleGuard([...rolesFor('/suporte-tecnico')]),
  component: SuporteTecnicoPage,
});

const estoqueRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/estoque',
  beforeLoad: createRoleGuard([...rolesFor('/estoque')]),
  component: EstoquePage,
});

const helpDeskRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/help-desk',
  beforeLoad: createRoleGuard([...rolesFor('/help-desk')]),
  component: HelpDeskPage,
});

// ═══════════════════════════════════════════
// CADASTROS
// ═══════════════════════════════════════════

const designadoresRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/admin/designadores',
  beforeLoad: createRoleGuard([...rolesFor('/admin/designadores')]),
  component: DesignadoresPage,
});

const empresasRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/admin/empresas',
  beforeLoad: createRoleGuard([...rolesFor('/admin/empresas')]),
  component: EmpresasPage,
});

const filiaisRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/admin/filiais',
  beforeLoad: createRoleGuard([...rolesFor('/admin/filiais')]),
  component: FiliaisPage,
  validateSearch: (search: Record<string, unknown>) => ({
    empresaId: typeof search.empresaId === 'string' && search.empresaId ? search.empresaId : undefined,
  }),
});

/** Locais deixaram de existir: endereço e ambiente de instalação ficam na filial. */
const locaisRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/admin/locais',
  beforeLoad: () => {
    // eslint-disable-next-line @typescript-eslint/only-throw-error -- TanStack redirect() is the idiomatic control-flow throw
    throw redirect({ to: '/admin/filiais', search: { empresaId: undefined } });
  },
});

const pessoasRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/admin/pessoas',
  beforeLoad: createRoleGuard([...rolesFor('/admin/pessoas')]),
  component: PessoasPage,
  validateSearch: (
    search: Record<string, unknown>,
  ): { tab?: 'usuarios' | 'operadores' | undefined; deviceId?: string | undefined } => ({
    tab: search.tab === 'usuarios' || search.tab === 'operadores' ? search.tab : undefined,
    deviceId: typeof search.deviceId === 'string' && search.deviceId ? search.deviceId : undefined,
  }),
});

const novoEquipamentoRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/admin/dispositivos/novo',
  beforeLoad: createRoleGuard([...rolesFor('/admin/dispositivos/novo')]),
  component: NovoEquipamentoPage,
  validateSearch: (search: Record<string, unknown>) => ({
    from: typeof search.from === 'string' ? search.from : undefined,
  }),
});

const editarEquipamentoRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/admin/dispositivos/$deviceId/editar',
  beforeLoad: createRoleGuard([...rolesFor('/admin/dispositivos/$deviceId/editar')]),
  component: NovoEquipamentoPage,
  validateSearch: (search: Record<string, unknown>) => ({
    from: typeof search.from === 'string' ? search.from : undefined,
  }),
});

const dispositivosRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/admin/dispositivos',
  beforeLoad: createRoleGuard([...rolesFor('/admin/dispositivos')]),
  component: DevicesPage,
});

const utilizadoresRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/admin/utilizadores',
  beforeLoad: createRoleGuard([...rolesFor('/admin/utilizadores')]),
  component: UtilizadoresPage,
});

const capacidadesRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/admin/capacidades',
  beforeLoad: createRoleGuard([...rolesFor('/admin/capacidades')]),
  component: CapacidadesPage,
});

/** Permissões de operadores foram para o cadastro do operador (Pessoas > Operadores). */
const associacaoEquipamentosRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/admin/associar-equipamentos',
  beforeLoad: () => {
    // eslint-disable-next-line @typescript-eslint/only-throw-error -- TanStack redirect() is the idiomatic control-flow throw
    throw redirect({ to: '/admin/pessoas', search: { tab: 'operadores', deviceId: undefined } });
  },
});

const configuracoesRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/admin/configuracoes',
  beforeLoad: createRoleGuard([...rolesFor('/admin/configuracoes')]),
  component: ConfiguracoesPage,
});

// ═══════════════════════════════════════════
// SISTEMA
// ═══════════════════════════════════════════

const configPlataformaRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/sistema/config',
  beforeLoad: createRoleGuard([...rolesFor('/sistema/config')]),
  component: ConfigPlataformaPage,
});

const otaFirmwareRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/sistema/ota',
  beforeLoad: createRoleGuard([...rolesFor('/sistema/ota')]),
  component: OtaFirmwarePage,
});

const lgpdRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/lgpd',
  beforeLoad: createRoleGuard([...rolesFor('/lgpd')]),
  component: LgpdPage,
});

const auditRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/audit',
  beforeLoad: createRoleGuard([...rolesFor('/audit')]),
  component: AuditPage,
});

// ═══════════════════════════════════════════
// DEVELOPER
// ═══════════════════════════════════════════

const apiDocsRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/developer/docs',
  beforeLoad: createRoleGuard([...rolesFor('/developer/docs')]),
  component: ApiDocsPage,
});

const apiKeysRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/developer/keys',
  beforeLoad: createRoleGuard([...rolesFor('/developer/keys')]),
  component: ApiKeysPage,
});

const webhooksRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/developer/webhooks',
  beforeLoad: createRoleGuard([...rolesFor('/developer/webhooks')]),
  component: WebhooksPage,
});

const sandboxRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/developer/sandbox',
  beforeLoad: createRoleGuard([...rolesFor('/developer/sandbox')]),
  component: SandboxPage,
});

const eventLogRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/developer/events',
  beforeLoad: createRoleGuard([...rolesFor('/developer/events')]),
  component: EventLogPage,
});

// ═══════════════════════════════════════════
// REDIRECTS (backward compatibility)
// ═══════════════════════════════════════════

/** Old /devices → new /equipamentos */
const devicesRedirect = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/devices',
  beforeLoad: () => {
    // eslint-disable-next-line @typescript-eslint/only-throw-error -- TanStack redirect() is the idiomatic control-flow throw
    throw redirect({ to: '/equipamentos', search: { tab: undefined } });
  },
});

/** Old /map → dashboard (removed from nav, keep route functional) */
const mapRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/map',
  beforeLoad: createRoleGuard([...rolesFor('/map')]),
  component: MapPage,
});

/** Old /users → /admin/pessoas */
const usersRedirect = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/users',
  beforeLoad: () => {
    // eslint-disable-next-line @typescript-eslint/only-throw-error -- TanStack redirect() is the idiomatic control-flow throw
    throw redirect({ to: '/admin/pessoas' });
  },
});

// ═══════════════════════════════════════════
// PUBLIC ROUTES (no AppShell)
// ═══════════════════════════════════════════

const forbiddenRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/403',
  component: ForbiddenPage,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginPage,
});

/** Route tree */
const routeTree = rootRoute.addChildren([
  layoutRoute.addChildren([
    // Monitoramento
    dashboardRoute,
    sensoresRoute,
    financeiroRoute,
    equipamentosListRoute,    equipamentoDetailRoute,
    alertsRoute,
    coletasRoute,
    // Relatórios
    reportsRoute,
    caixaRedirect,
    // aiRoute, // Hidden until AI feature is complete (see handoff.md)
    suporteTecnicoRoute,
    estoqueRoute,
    helpDeskRoute,
    // Cadastros
    designadoresRoute,
    empresasRoute,
    filiaisRoute,
    locaisRoute,
    pessoasRoute,
    novoEquipamentoRoute,
    editarEquipamentoRoute,
    dispositivosRoute,
    utilizadoresRoute,
    capacidadesRoute,
    associacaoEquipamentosRoute,
    configuracoesRoute,
    // Sistema
    configPlataformaRoute,
    otaFirmwareRoute,
    lgpdRoute,
    auditRoute,
    // Developer
    apiDocsRoute,
    apiKeysRoute,
    webhooksRoute,
    sandboxRoute,
    eventLogRoute,
    // Redirects (backward compat)
    devicesRedirect,
    mapRoute,
    usersRedirect,
  ]),
  forbiddenRoute,
  loginRoute,
]);

/**
 * The application router instance.
 */
export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  defaultNotFoundComponent: () => {
    // eslint-disable-next-line @typescript-eslint/only-throw-error -- TanStack redirect() is the idiomatic control-flow throw
    throw redirect({ to: '/403' });
  },
});

/** Type declaration for TanStack Router */
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
